import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type {
  AgentProfileCreateRequestDto,
  AgentProfileResponseDto,
  TaskFlowDto,
} from '@shared/services/apiService/apiService';
import { useWorkspaceScope } from './useWorkspaceScope';
import type { WorkspaceJobMetadata } from './jobMetadata';

export type GuidedJobPlan = {
  mode: 'new' | 'copy';
  sourceId?: string;
  values: AgentProfileCreateRequestDto & WorkspaceJobMetadata;
  taskFlow: TaskFlowDto | null;
};
export type GuidedCreationReceipt = {
  plan: GuidedJobPlan;
  profile: AgentProfileResponseDto | null;
  workflow: TaskFlowDto | null;
  workflowSaved: boolean;
  phase:
    | 'job-pending'
    | 'job-unknown'
    | 'job-changed'
    | 'workflow-pending'
    | 'ready'
    | 'agent-pending'
    | 'agent-unknown'
    | 'completed';
  pending: boolean;
  error: string;
};
const createReceiptStore = () => {
  const rows = new Map<string, GuidedCreationReceipt>();
  const listeners = new Map<string, Set<() => void>>();
  let active = true;
  return {
    activate: () => {
      active = true;
    },
    dispose: () => {
      active = false;
      rows.clear();
    },
    get: (key: string) => rows.get(key),
    set: (key: string, value: GuidedCreationReceipt | null) => {
      if (!active) return false;
      if (value) rows.set(key, value);
      else rows.delete(key);
      listeners.get(key)?.forEach((notify) => notify());
      return true;
    },
    subscribe: (key: string, notify: () => void) => {
      const group = listeners.get(key) ?? new Set<() => void>();
      group.add(notify);
      listeners.set(key, group);
      return () => {
        group.delete(notify);
        if (!group.size) listeners.delete(key);
      };
    },
  };
};
const Context = createContext<ReturnType<typeof createReceiptStore> | null>(null);

/** Acknowledged native receipts survive navigation; switching principal disposes the whole owner. */
export const GuidedCreationReceiptProvider = ({ children }: { children: ReactNode }) => {
  const scope = useWorkspaceScope();
  return <ReceiptOwner key={scope}>{children}</ReceiptOwner>;
};
const ReceiptOwner = ({ children }: { children: ReactNode }) => {
  const [store] = useState(createReceiptStore);
  useEffect(() => {
    store.activate();
    return () => store.dispose();
  }, [store]);
  return <Context.Provider value={store}>{children}</Context.Provider>;
};
export const useGuidedCreationReceipt = (key: string) => {
  const store = useContext(Context);
  if (!store) throw new Error('Guided creation requires its principal-scoped receipt provider.');
  const subscribe = useCallback((notify: () => void) => store.subscribe(key, notify), [store, key]);
  const snapshot = useCallback(() => store.get(key), [store, key]);
  const receipt = useSyncExternalStore(subscribe, snapshot, snapshot);
  const setReceipt = useCallback(
    (value: GuidedCreationReceipt | null) => store.set(key, value),
    [store, key],
  );
  return { receipt, setReceipt, getReceipt: snapshot };
};
